import React, { useState, useMemo, useEffect } from 'react';
import Markdown from 'markdown-to-jsx';
import { ChevronDown, Search, ExternalLink, Globe, Edit3, Sparkles, Save, Loader2, FilePlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CompanyPage, PageUpdate } from '@/hooks/useManagementAPI';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { ClaudeTaskCommand } from '@/types/claudeTask';
import { useToast } from '@/hooks/use-toast';
import { invokeEdgeFunction } from '@/utils/edgeFunctionUtils';
import { fetchMultiYearOCR, fetchOCRText, truncateOCRText } from '@/utils/ocrHelper';
import { PricingHistoryTimeline } from './PricingHistoryTimeline';
import { ScreenshotHistory } from './ScreenshotHistory';
import { PlansSection } from './PlansSection';
import { useCompanyPricingHistory } from '@/hooks/useCompanyPricingHistory';

interface PageSectionProps {
  pages: CompanyPage[];
  selectedPageId: string | null;
  onSelectPage: (pageId: string) => void;
  loading?: boolean;
  companySlug: string;
  onUpdatePage: (pageId: string, updates: PageUpdate) => Promise<CompanyPage | null>;
  onUpdateSuccess?: () => void;
  onUpdateError?: (error: string) => void;
}

const MAX_VISIBLE_TABS = 3;
const MAX_NAME_LENGTH = 25;

export const PageSection: React.FC<PageSectionProps> = ({
  pages,
  selectedPageId,
  onSelectPage,
  loading = false,
  companySlug,
  onUpdatePage,
  onUpdateSuccess,
  onUpdateError,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedHistorySummary, setEditedHistorySummary] = useState('');
  const [isEditingHistorySummary, setIsEditingHistorySummary] = useState(false);
  const [editedPricingUrl, setEditedPricingUrl] = useState('');
  const [isEditingPricingUrl, setIsEditingPricingUrl] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiGeneratingHistory, setAiGeneratingHistory] = useState(false);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const [addPageOpen, setAddPageOpen] = useState(false);
  const [addPageSlug, setAddPageSlug] = useState('');
  const [addPageUrl, setAddPageUrl] = useState('');
  const [addPageNavHint, setAddPageNavHint] = useState('');
  const [addPageSubmitting, setAddPageSubmitting] = useState(false);
  const { toast } = useToast();

  // Get pricing history events for this company
  const { events: pricingEvents } = useCompanyPricingHistory(companySlug);

  // Format subslug for display
  const formatTabName = (page: CompanyPage): string => {
    if (!page.subslug || page.subslug === '') {
      return 'Main';
    }
    const name = page.subslug.replace(/^\./, '');
    return name
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Truncate name if too long
  const truncateName = (name: string): string => {
    if (name.length <= MAX_NAME_LENGTH) return name;
    return name.slice(0, MAX_NAME_LENGTH) + '...';
  };

  // Ensure pages is an array and filter out archived pages
  const visiblePages = Array.isArray(pages) ? pages.filter(page => !page.archived) : [];

  // Split into visible tabs and dropdown items
  const visibleTabs = visiblePages.slice(0, MAX_VISIBLE_TABS);
  const dropdownPages = visiblePages.slice(MAX_VISIBLE_TABS);

  // Check if selected page is in dropdown
  const selectedInDropdown = dropdownPages.some(p => p.id === selectedPageId);
  const selectedDropdownPage = dropdownPages.find(p => p.id === selectedPageId);
  const selectedPage = visiblePages.find(p => p.id === selectedPageId) || null;

  // Filter dropdown pages by search query
  const filteredDropdownPages = useMemo(() => {
    if (!searchQuery.trim()) return dropdownPages;
    const query = searchQuery.toLowerCase();
    return dropdownPages.filter(page =>
      formatTabName(page).toLowerCase().includes(query)
    );
  }, [dropdownPages, searchQuery]);

  // Sync edited fields when selected page changes
  useEffect(() => {
    if (selectedPage) {
      setEditedDescription(selectedPage.product_description || '');
      setEditedHistorySummary(selectedPage.history_summary || '');
      setEditedPricingUrl(selectedPage.pricing_page_url || '');
      setIsEditingDescription(false);
      setIsEditingHistorySummary(false);
      setIsEditingPricingUrl(false);
    }
  }, [selectedPage?.id, selectedPage?.product_description, selectedPage?.history_summary, selectedPage?.pricing_page_url]);

  // Check if any field has changed (dirty flag)
  const isDirty = selectedPage
    ? editedDescription !== (selectedPage.product_description || '') ||
      editedHistorySummary !== (selectedPage.history_summary || '') ||
      editedPricingUrl !== (selectedPage.pricing_page_url || '')
    : false;

  // Handle save
  const handleSave = async () => {
    if (!selectedPage || !isDirty) return;

    setIsSaving(true);
    try {
      const updates: { product_description?: string; history_summary?: string; pricing_page_url?: string } = {};
      if (editedDescription !== (selectedPage.product_description || '')) {
        updates.product_description = editedDescription;
      }
      if (editedHistorySummary !== (selectedPage.history_summary || '')) {
        updates.history_summary = editedHistorySummary;
      }
      if (editedPricingUrl !== (selectedPage.pricing_page_url || '')) {
        updates.pricing_page_url = editedPricingUrl;
      }
      await onUpdatePage(selectedPage.id, updates);
      onUpdateSuccess?.();
      setIsEditingDescription(false);
      setIsEditingHistorySummary(false);
      setIsEditingPricingUrl(false);
    } catch (error) {
      console.error('[PageSection] Save error:', error);
      onUpdateError?.(error instanceof Error ? error.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // AI generation for product description using OCR from pricing page
  const handleAIGenerate = async () => {
    if (!selectedPage) return;

    setAiGenerating(true);
    try {
      // Fetch OCR text from the pricing page
      const compositeSlug = selectedPage.subslug
        ? `${companySlug}.${selectedPage.subslug}`
        : companySlug;

      const ocrText = await fetchOCRText(compositeSlug);

      if (!ocrText) {
        throw new Error('No OCR content available for this page');
      }

      // Truncate OCR to fit in prompt
      const truncatedOCR = truncateOCRText(ocrText, 6000);

      const { data: aiData, error: aiError } = await invokeEdgeFunction(
        'ai-composer',
        {
          systemPrompt: `You are a product analyst. Write clear, factual product descriptions based on pricing page content. Avoid marketing fluff and superlatives. Be specific about what the product does.`,
          userPrompt: `Based on this pricing page content, write a concise product description that answers:

1. **What is it?** - What type of product/service is this? (e.g., "API management platform", "team collaboration tool")
2. **Who is it for?** - Target users/customers (e.g., "developers", "enterprise teams", "small businesses")
3. **Main value prop** - The primary benefit or problem it solves
4. **Key features** - 2-3 standout capabilities mentioned on the pricing page

Format as 2-3 sentences, not bullet points. Use the exact product name as shown on the page.

Example output:
"Kong Konnect is an API management platform for developers and platform teams. It provides API gateway, developer portal, and analytics capabilities to help organizations manage, secure, and monitor their APIs at scale. Key features include rate limiting, authentication plugins, and multi-cloud deployment options."

Pricing page content:
${truncatedOCR}`,
          maxTokens: 200,
          temperature: 0.3,
        }
      );

      if (aiError) {
        throw new Error(aiError.message || 'AI generation failed');
      }

      setEditedDescription(aiData.text);
    } catch (error: unknown) {
      console.error('[PageSection] AI generation error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate description with AI';
      onUpdateError?.(errorMessage);
    } finally {
      setAiGenerating(false);
    }
  };

  // AI generation for history summary using events + multi-year OCR
  const handleAIGenerateHistory = async () => {
    if (!selectedPage) return;

    setAiGeneratingHistory(true);
    try {
      // Fetch OCR from multiple years in parallel
      const multiYearOCR = await fetchMultiYearOCR(companySlug, selectedPage.subslug || undefined);

      // Format events for the prompt
      const eventsText = pricingEvents.length > 0
        ? pricingEvents.map(e => {
            const date = e.created_at ? new Date(e.created_at).toLocaleDateString() : '';
            const category = e.category || 'misc';
            const description = e.event_description || e.hook || 'Pricing change';
            return `- [${date}] (${category}) ${description}`;
          }).join('\n')
        : 'No pricing events recorded.';

      // Format multi-year OCR (full text, no truncation)
      const years = Object.keys(multiYearOCR).sort().reverse(); // Newest first
      const ocrSections = years.map(year => {
        return `=== ${year} Pricing Page ===\n${multiYearOCR[year]}`;
      }).join('\n\n');

      const combinedContext = `PRICING EVENTS:\n${eventsText}\n\nPRICING PAGE SNAPSHOTS BY YEAR:\n${ocrSections}`;

      const { data: aiData, error: aiError } = await invokeEdgeFunction(
        'ai-composer',
        {
          systemPrompt: `You are a senior pricing strategist. Write strategic analyses using PROPER MARKDOWN SYNTAX.

CRITICAL FORMATTING RULES:
- Use ## for main section headers (e.g., "## Strategic Overview")
- Use ### for sub-headers (e.g., "### 2023")
- Use - for bullet points (with a space after the dash)
- Leave a blank line between sections
- Leave a blank line before and after lists

Example output format:
## Strategic Overview

Company X has evolved its pricing strategy significantly.

### 2023
- Introduced usage-based pricing
- Launched enterprise tier

### 2024
- Simplified to 3 tiers
- Added AI features to premium plans

## Pricing Rationale

- **Tier simplification**: Reduced confusion for buyers
- **Usage-based model**: Aligns cost with value delivered

## Key Takeaways

- Moving upmarket toward enterprise
- Consolidating around core value props`,
          userPrompt: `Analyze this company's pricing evolution. Use proper markdown with ## headers, ### sub-headers, and - bullet points. Include blank lines between sections:\n\n${combinedContext}`,
          maxTokens: 2500,
          temperature: 0.5,
          model: 'gpt-4o',
        }
      );

      if (aiError) {
        throw new Error(aiError.message || 'AI generation failed');
      }

      setEditedHistorySummary(aiData.text);
    } catch (error: unknown) {
      console.error('[PageSection] AI history generation error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate history summary with AI';
      onUpdateError?.(errorMessage);
    } finally {
      setAiGeneratingHistory(false);
    }
  };

  const handleAddPageSubmit = async () => {
    if (!addPageSlug.trim() || !addPageUrl.trim()) return;
    setAddPageSubmitting(true);
    try {
      const params: Record<string, any> = {
        company_slug: companySlug,
        page_slug: addPageSlug.trim(),
        pricing_page_url: addPageUrl.trim(),
      };
      if (addPageNavHint.trim()) {
        params.navigation_hint = addPageNavHint.trim();
      }
      const { error } = await supabase
        .from('claude_tasks')
        .insert({
          command: ClaudeTaskCommand.RUN_ADD_PAGE,
          slug: companySlug,
          params,
          status: 'pending',
          priority: 5,
        });
      if (error) throw error;
      toast({ title: 'Task created', description: `Add page "${addPageSlug.trim()}" task created for ${companySlug}` });
      setAddPageOpen(false);
      setAddPageSlug('');
      setAddPageUrl('');
      setAddPageNavHint('');
    } catch (error: any) {
      const msg = error?.code === '23505'
        ? 'A pending add-page task already exists for this company.'
        : (error?.message || 'Failed to create task');
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setAddPageSubmitting(false);
    }
  };

  if (loading) {
    return <PageSectionSkeleton />;
  }

  // Show empty state instead of returning null
  if (visiblePages.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center text-gray-500">
          No pages found for this company
        </div>
      </div>
    );
  }

  const renderTab = (page: CompanyPage) => {
    const fullName = formatTabName(page);
    const displayName = truncateName(fullName);
    const needsTooltip = fullName.length > MAX_NAME_LENGTH;
    const isSelected = selectedPageId === page.id;

    const tabButton = (
      <button
        key={page.id}
        onClick={() => onSelectPage(page.id)}
        className={cn(
          'px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap border-b-2 -mb-px',
          isSelected
            ? 'border-blue-600 text-blue-700 bg-white'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
        )}
      >
        {displayName}
      </button>
    );

    if (needsTooltip) {
      return (
        <Tooltip key={page.id}>
          <TooltipTrigger asChild>
            {tabButton}
          </TooltipTrigger>
          <TooltipContent>
            <p>{fullName}</p>
          </TooltipContent>
        </Tooltip>
      );
    }

    return tabButton;
  };

  return (
    <TooltipProvider>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Tab Navigation Header */}
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="flex items-center px-2">
            {/* Visible tabs */}
            {visibleTabs.map(renderTab)}

            {/* Add Page Button */}
            <Dialog open={addPageOpen} onOpenChange={setAddPageOpen}>
              <DialogTrigger asChild>
                <button
                  className="flex items-center gap-1 px-3 py-2.5 text-sm font-medium text-teal-600 hover:text-teal-800 hover:bg-teal-50 transition-colors whitespace-nowrap border-b-2 border-transparent -mb-px ml-auto"
                  title="Add a new sub-page"
                >
                  <FilePlus className="h-4 w-4" />
                </button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[450px]">
                <DialogHeader>
                  <DialogTitle>Add Sub-Page</DialogTitle>
                  <DialogDescription>
                    Add a new page to <span className="font-medium">{companySlug}</span>
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-1.5">
                    <Label htmlFor="addPageSlug">Page Slug *</Label>
                    <Input
                      id="addPageSlug"
                      value={addPageSlug}
                      onChange={(e) => setAddPageSlug(e.target.value)}
                      placeholder="e.g., enterprise"
                      autoFocus
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="addPageUrl">Pricing Page URL *</Label>
                    <Input
                      id="addPageUrl"
                      value={addPageUrl}
                      onChange={(e) => setAddPageUrl(e.target.value)}
                      placeholder="https://example.com/pricing"
                      type="url"
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor="addPageNavHint">Navigation Hint <span className="text-muted-foreground font-normal">(optional)</span></Label>
                    <Input
                      id="addPageNavHint"
                      value={addPageNavHint}
                      onChange={(e) => setAddPageNavHint(e.target.value)}
                      placeholder="e.g., click on the Enterprise tab"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" size="sm" onClick={() => setAddPageOpen(false)} disabled={addPageSubmitting}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleAddPageSubmit} disabled={addPageSubmitting || !addPageSlug.trim() || !addPageUrl.trim()}>
                    {addPageSubmitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FilePlus className="h-4 w-4 mr-1" />}
                    Create Task
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Dropdown for remaining pages */}
            {dropdownPages.length > 0 && (
              <DropdownMenu onOpenChange={(open) => !open && setSearchQuery('')}>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      'flex items-center gap-1 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap border-b-2 -mb-px',
                      selectedInDropdown
                        ? 'border-blue-600 text-blue-700 bg-white'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    )}
                  >
                    {selectedInDropdown && selectedDropdownPage
                      ? truncateName(formatTabName(selectedDropdownPage))
                      : `+${dropdownPages.length} more`}
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {/* Search input */}
                  <div className="px-2 py-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search pages..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-8 h-8 text-sm"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {filteredDropdownPages.length === 0 ? (
                      <div className="px-2 py-3 text-sm text-gray-500 text-center">
                        No pages found
                      </div>
                    ) : filteredDropdownPages.map((page) => {
                      const fullName = formatTabName(page);
                      const displayName = truncateName(fullName);
                      const needsTooltip = fullName.length > MAX_NAME_LENGTH;

                      if (needsTooltip) {
                        return (
                          <Tooltip key={page.id}>
                            <TooltipTrigger asChild>
                              <DropdownMenuItem
                                onClick={() => onSelectPage(page.id)}
                                className={cn(
                                  selectedPageId === page.id && 'bg-blue-50 text-blue-700'
                                )}
                              >
                                {displayName}
                              </DropdownMenuItem>
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              <p>{fullName}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      }

                      return (
                        <DropdownMenuItem
                          key={page.id}
                          onClick={() => onSelectPage(page.id)}
                          className={cn(
                            selectedPageId === page.id && 'bg-blue-50 text-blue-700'
                          )}
                        >
                          {displayName}
                        </DropdownMenuItem>
                      );
                    })}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Page Content */}
        <div className="p-4">
          {selectedPage ? (
            <div className="space-y-4">
              {/* Save Section */}
              <div className="flex items-center justify-end gap-2">
                {isDirty && (
                  <span className="text-sm text-amber-600">Unsaved changes</span>
                )}
                <Button
                  onClick={handleSave}
                  disabled={!isDirty || isSaving}
                  size="sm"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-1" />
                  )}
                  Save
                </Button>
              </div>

              {/* Description Row */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Product Description
                  </div>
                </div>
                {isEditingDescription ? (
                  <div className="relative">
                    <Textarea
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      onBlur={() => !isDirty && !aiGenerating && setIsEditingDescription(false)}
                      placeholder="Enter product description..."
                      rows={3}
                      className="resize-none text-sm"
                      autoFocus
                    />
                    {/* Show spinner while AI is generating in edit mode */}
                    {aiGenerating && (
                      <div className="absolute top-2 right-2">
                        <Loader2 className="h-4 w-4 text-purple-500 animate-spin" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className="cursor-pointer group relative min-h-[2.5rem]"
                    onClick={() => setIsEditingDescription(true)}
                  >
                    <p className="text-sm text-gray-600 pr-16 whitespace-pre-wrap">
                      {editedDescription || (
                        <span className="text-gray-400 italic">Click to add description...</span>
                      )}
                    </p>
                    {/* Show buttons on hover OR always when AI is generating */}
                    <div className={`absolute top-0 right-0 flex items-center gap-1 transition-opacity ${aiGenerating ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAIGenerate();
                        }}
                        disabled={aiGenerating}
                        title="Generate with AI"
                      >
                        {aiGenerating ? (
                          <Loader2 className="h-3.5 w-3.5 text-purple-500 animate-spin" />
                        ) : (
                          <Sparkles className="h-3.5 w-3.5 text-purple-500" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsEditingDescription(true);
                        }}
                        title="Edit"
                      >
                        <Edit3 className="h-3.5 w-3.5 text-gray-400" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Pricing Page URL Row */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Globe className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">
                      Pricing Page URL
                    </div>
                    {isEditingPricingUrl ? (
                      <Input
                        value={editedPricingUrl}
                        onChange={(e) => setEditedPricingUrl(e.target.value)}
                        onBlur={() => setIsEditingPricingUrl(false)}
                        placeholder="https://example.com/pricing"
                        className="text-sm"
                        autoFocus
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        {editedPricingUrl ? (
                          <a
                            href={editedPricingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate"
                          >
                            {editedPricingUrl}
                          </a>
                        ) : (
                          <span className="text-sm text-gray-400 italic">No URL set</span>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 flex-shrink-0"
                          onClick={() => setIsEditingPricingUrl(true)}
                          title="Edit URL"
                        >
                          <Edit3 className="h-3.5 w-3.5 text-gray-400" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Pricing Plans Section */}
              <div className="border-t pt-4">
                <PlansSection slug={selectedPage.subslug ? `${companySlug}.${selectedPage.subslug.replace(/^\./, '')}` : companySlug} />
              </div>

              {/* History Summary Row - Collapsible */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                    className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide hover:text-gray-700 transition-colors"
                  >
                    <ChevronDown className={cn(
                      "h-4 w-4 transition-transform",
                      !isHistoryExpanded && "-rotate-90"
                    )} />
                    Pricing History Summary
                  </button>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAIGenerateHistory();
                      }}
                      disabled={aiGeneratingHistory}
                      title="Generate with AI"
                    >
                      {aiGeneratingHistory ? (
                        <Loader2 className="h-3.5 w-3.5 text-purple-500 animate-spin" />
                      ) : (
                        <Sparkles className="h-3.5 w-3.5 text-purple-500" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEditingHistorySummary(true);
                      }}
                      title="Edit"
                    >
                      <Edit3 className="h-3.5 w-3.5 text-gray-400" />
                    </Button>
                  </div>
                </div>
                {isEditingHistorySummary ? (
                  <div className="relative">
                    <Textarea
                      value={editedHistorySummary}
                      onChange={(e) => setEditedHistorySummary(e.target.value)}
                      onBlur={() => !aiGeneratingHistory && setIsEditingHistorySummary(false)}
                      placeholder="Enter pricing history summary..."
                      rows={12}
                      className="resize-y text-sm min-h-[200px] max-h-[600px]"
                      autoFocus
                    />
                    {aiGeneratingHistory && (
                      <div className="absolute top-2 right-2">
                        <Loader2 className="h-4 w-4 text-purple-500 animate-spin" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="relative">
                    {editedHistorySummary ? (
                      <>
                        <div
                          className={cn(
                            "overflow-hidden transition-all duration-300 cursor-pointer",
                            isHistoryExpanded ? "max-h-none" : "max-h-[180px]"
                          )}
                          onClick={() => setIsEditingHistorySummary(true)}
                        >
                          <Markdown
                            options={{
                              overrides: {
                                h2: {
                                  component: 'h2',
                                  props: { className: 'text-xs font-semibold uppercase tracking-wider text-gray-500 mt-6 mb-3 first:mt-0 border-b border-gray-100 pb-2' }
                                },
                                h3: {
                                  component: 'h3',
                                  props: { className: 'text-sm font-semibold text-gray-700 mt-4 mb-2' }
                                },
                                p: {
                                  component: 'p',
                                  props: { className: 'text-sm text-gray-600 leading-relaxed my-3' }
                                },
                                ul: {
                                  component: 'ul',
                                  props: { className: 'list-disc list-outside ml-5 my-3 space-y-2' }
                                },
                                li: {
                                  component: 'li',
                                  props: { className: 'text-sm text-gray-600 leading-relaxed' }
                                },
                                strong: {
                                  component: 'strong',
                                  props: { className: 'font-semibold text-gray-700' }
                                },
                              }
                            }}
                          >
                            {editedHistorySummary}
                          </Markdown>
                        </div>
                        {!isHistoryExpanded && (
                          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                        )}
                        <button
                          onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium mt-2"
                        >
                          {isHistoryExpanded ? 'Show less' : 'Show more'}
                        </button>
                      </>
                    ) : (
                      <p
                        className="text-sm text-gray-400 italic cursor-pointer"
                        onClick={() => setIsEditingHistorySummary(true)}
                      >
                        Click to add pricing history summary...
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Pricing History Timeline */}
              <div className="border-t pt-4">
                <PricingHistoryTimeline
                  companySlug={companySlug}
                />
              </div>

              {/* Screenshot History */}
              <div className="border-t pt-4">
                <ScreenshotHistory
                  companySlug={companySlug}
                  subslug={selectedPage?.subslug || undefined}
                />
              </div>

            </div>
          ) : (
            <div className="text-sm text-gray-500 text-center py-2">
              Select a page to view details
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

// Loading Skeleton
export const PageSectionSkeleton: React.FC = () => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Tab header skeleton */}
      <div className="bg-gray-50 border-b border-gray-200 px-2 py-2">
        <div className="flex gap-2">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-16" />
        </div>
      </div>
      {/* Content skeleton */}
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <div className="space-y-2 flex-1">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <div className="border-t pt-4">
          <Skeleton className="h-3 w-32 mb-2" />
          <Skeleton className="h-16 w-full" />
        </div>
      </div>
    </div>
  );
};

export default PageSection;
